import type { TgnTotalizerProps } from '~components/TgnTotalizer';
import type {
  CompanyDocument,
  PersonalDocument,
} from '@scansource-brazil/zeus-core-ui/utils';

import { getObjectKeys, isEmptyValue } from '@scansource-brazil/zeus-core-ui/data';
import { isNullOrUndefined } from '~utils/dataUtils';
import { dateStringToModel, formatCurrency, formatPercentage } from './helpers';
import { COMMISSION_STATUS } from '~helpers/Commissions.const';

/**
 * Represents the totalizer properties for sales commissions,
 * including the title, value, and other optional properties.
 * Used to display summarized financial information in a user interface.
 */
export type SalesCommissionsTotalizer = TgnTotalizerProps;

/**
 * Represents a Data Transfer Object (DTO) for sales commission installment,
 * including installment number, amount, due date, and net value.
 */
export type SalesCommissionInstallmentDto = {
  installment: number | null;
  installmentValue: number | null;
  dueDate: string | null;
  installmentCommissionNetValue: number | null;
};

/**
 * Represents a sales commission installment, including installment number, amount, due date, and net value.
 * Used to store detailed information about each installment in a sales commission.
 */
export class SalesCommissionInstallment {
  installment: string = '';
  amount: string = '';
  dueDate: Date | string = '';
  netValue: string = '';

  constructor(data?: SalesCommissionInstallment | SalesCommissionInstallmentDto) {
    if (!data) return;
    if (!(data instanceof SalesCommissionInstallment)) this.fromDto(data);
    else Object.assign(this, data);
  }

  /**
   * Sets the properties of the SalesCommissionInstallment instance based on the provided DTO.
   * This method is useful for initializing or updating the instance with data from an API response.
   * @param dto - The SalesCommissionInstallmentDto object containing the data to set.
   * @returns The updated SalesCommissionInstallment instance.
   */
  fromDto(dto: Partial<SalesCommissionInstallmentDto>): SalesCommissionInstallment {
    this.installment = !dto.installment ? '-' : dto.installment.toString();
    this.amount = formatCurrency(dto.installmentValue);
    this.dueDate = dateStringToModel(dto.dueDate);
    this.netValue = formatCurrency(dto.installmentCommissionNetValue);
    return this;
  }

  /**
   * Adapts the SalesCommissionInstallment from a DTO to a SalesCommissionInstallment instance.
   * @param dto - The SalesCommissionInstallmentDto object containing the data to adapt.
   * @returns The adapted SalesCommissionInstallment instance.
   */
  toRaw(): SalesCommissionInstallment {
    return { ...this };
  }
}

/**
 * Represents the totals of sales commission installments, including the total amount and net value.
 * Used to summarize the financial details of sales commission installments.
 */
export type SalesCommissionInstallmentTotalsDto = {
  installmentsValueAmount: number | null;
  installmentsCommissionNetValueAmount: number | null;
};

/**
 * Represents the totals of sales commission installments, including the total amount and net value.
 * Provides methods to initialize, update, and clear data from DTOs or other SalesCommissionInstallmentTotals instances.
 */
export class SalesCommissionInstallmentTotals {
  installmentsAmount: string = '-';
  netValueAmount: string = '-';

  constructor(
    data?: SalesCommissionInstallmentTotals | SalesCommissionInstallmentTotalsDto,
  ) {
    if (!data) return;
    if (!(data instanceof SalesCommissionInstallmentTotals)) this.fromDto(data);
    else Object.assign(this, data);
  }

  /**
   * Sets the properties of the SalesCommissionInstallmentTotals instance based on the provided DTO.
   * This method is useful for initializing or updating the instance with data from an API response.
   * @param dto - The SalesCommissionInstallmentTotalsDto object containing the data to set.
   * @returns The updated SalesCommissionInstallmentTotals instance.
   */
  fromDto(dto: SalesCommissionInstallmentTotalsDto): SalesCommissionInstallmentTotals {
    this.installmentsAmount = formatCurrency(dto.installmentsValueAmount);
    this.netValueAmount = formatCurrency(dto.installmentsCommissionNetValueAmount);

    return this;
  }

  /**
   * Returns a shallow copy of the current SalesCommissionInstallmentTotals instance.
   * Useful for serialization or manipulation without affecting the original object.
   */
  toRaw(): SalesCommissionInstallmentTotals {
    return { ...this };
  }
}

/**
 * Represents a sales commission item, including part number, amount, and various tax values and percentages.
 * Used to store detailed information about each item in a sales commission.
 */
export type SalesCommissionItemDto = {
  partNumber: string | null;
  amount: number | null;
  ipiValue: number | null;
  ipiPercentage: number | null;
  issValue: number | null;
  issPercentage: number | null;
  icmsValue: number | null;
  icmsPercentage: number | null;
  pisValue: number | null;
  pisPercentage: number | null;
  cofinsValue: number | null;
  cofinsPercentage: number | null;
};

/**
 * Represents a sales commission item, including part number, amount, and various tax values and percentages.
 * Provides methods to initialize, update, and clear data from DTOs or other SalesCommissionItem instances.
 */
export class SalesCommissionItem {
  partNumber: string = '';
  amount: string = '';
  ipiValue: string = '-';
  ipiPercentage: string = '';
  issValue: string = '-';
  issPercentage: string = '';
  icmsValue: string = '-';
  icmsPercentage: string = '';
  pisValue: string = '-';
  pisPercentage: string = '';
  cofinsValue: string = '-';
  cofinsPercentage: string = '';

  constructor(data?: SalesCommissionItem | SalesCommissionItemDto) {
    if (!data) return;
    if (!(data instanceof SalesCommissionItem)) this.fromDto(data);
    else Object.assign(this, data);
  }

  /**
   * Sets the properties of the SalesCommissionItem instance based on the provided DTO.
   * This method is useful for initializing or updating the instance with data from an API response.
   * @param dto - The SalesCommissionItemDto object containing the data to set.
   * @returns The updated SalesCommissionItem instance.
   */
  fromDto(dto: Partial<SalesCommissionItemDto>): SalesCommissionItem {
    this.partNumber = dto.partNumber ?? '';
    this.amount = dto.amount?.toString() ?? '-';
    this.ipiValue = formatCurrency(dto.ipiValue);
    this.ipiPercentage = formatPercentage(dto.ipiPercentage);
    this.issValue = formatCurrency(dto.issValue);
    this.issPercentage = formatPercentage(dto.issPercentage);
    this.icmsValue = formatCurrency(dto.icmsValue);
    this.icmsPercentage = formatPercentage(dto.icmsPercentage);
    this.pisValue = formatCurrency(dto.pisValue);
    this.pisPercentage = formatPercentage(dto.pisPercentage);
    this.cofinsValue = formatCurrency(dto.cofinsValue);
    this.cofinsPercentage = formatPercentage(dto.cofinsPercentage);
    return this;
  }

  /**
   * Adapts the SalesCommissionItem from a DTO to a SalesCommissionItem instance.
   * @param dto - The SalesCommissionItemDto object containing the data to adapt.
   * @returns The adapted SalesCommissionItem instance.
   */
  toRaw(): SalesCommissionItem {
    return { ...this };
  }
}

/**
 * Represents the totals of sales commission items, including various tax values and percentages.
 * Used to summarize the financial details of sales commission items.
 */
export type SalesCommissionItemTotalsDto = {
  ipiTotalValue: number | null;
  ipiTotalPercentage: number | null;
  issTotalValue: number | null;
  issTotalPercentage: number | null;
  icmsTotalValue: number | null;
  icmsTotalPercentage: number | null;
  pisTotalValue: number | null;
  pisTotalPercentage: number | null;
  cofinsTotalValue: number | null;
  cofinsTotalPercentage: number | null;
};

/**
 * Represents the totals of sales commission items, including various tax values and percentages.
 * Provides methods to initialize, update, and clear data from DTOs or other SalesCommissionItemTotals instances.
 */
export class SalesCommissionItemTotals {
  ipiTotalValue: string = '-';
  ipiTotalPercentage: string = '';
  issTotalValue: string = '-';
  issTotalPercentage: string = '';
  icmsTotalValue: string = '-';
  icmsTotalPercentage: string = '';
  pisTotalValue: string = '-';
  pisTotalPercentage: string = '';
  cofinsTotalValue: string = '-';
  cofinsTotalPercentage: string = '';

  constructor(data?: SalesCommissionItemTotals | SalesCommissionItemTotalsDto) {
    if (!data) return;
    if (!(data instanceof SalesCommissionItemTotals)) this.fromDto(data);
    else Object.assign(this, data);
  }

  /**
   * Sets the properties of the SalesCommissionItemTotals instance based on the provided DTO.
   * This method is useful for initializing or updating the instance with data from an API response.
   * @param dto - The SalesCommissionItemTotalsDto object containing the data to set.
   * @returns The updated SalesCommissionItemTotals instance.
   */
  fromDto(dto: SalesCommissionItemTotalsDto): SalesCommissionItemTotals {
    if (isNullOrUndefined(dto) || isEmptyValue(dto)) return this;

    this.ipiTotalValue = formatCurrency(dto.ipiTotalValue);
    this.issTotalValue = formatCurrency(dto.issTotalValue);
    this.icmsTotalValue = formatCurrency(dto.icmsTotalValue);
    this.pisTotalValue = formatCurrency(dto.pisTotalValue);
    this.cofinsTotalValue = formatCurrency(dto.cofinsTotalValue);
    this.ipiTotalPercentage = formatPercentage(dto.ipiTotalPercentage);
    this.issTotalPercentage = formatPercentage(dto.issTotalPercentage);
    this.icmsTotalPercentage = formatPercentage(dto.icmsTotalPercentage);
    this.pisTotalPercentage = formatPercentage(dto.pisTotalPercentage);
    this.cofinsTotalPercentage = formatPercentage(dto.cofinsTotalPercentage);

    return this;
  }

  /**
   * Returns a shallow copy of the current SalesCommissionItemTotals instance.
   * Useful for serialization or manipulation without affecting the original object.
   */
  toRaw(): SalesCommissionItemTotals {
    return { ...this };
  }
}

/**
 * Represents the history of a sales commission installment, including the date credited,
 * installment number, net value, and status.
 */
export type SalesCommissionInstallmentHistoryDto = {
  creditedAt: string | null;
  installment: number | null;
  installmentCommissionNetValue: number | null;
  installmentStatus: number | null;
};

/**
 * Represents the history of a sales commission installment, including the date credited,
 * installment number, net value, and status.
 * Provides methods to initialize, update, and clear data from DTOs or other SalesCommissionInstallmentHistory instances.
 */
export class SalesCommissionInstallmentHistory {
  creditedAt: Date | string = '';
  installment: string = '-';
  installmentCommissionNetValue: string = '-';
  installmentStatus: { color: string; content: string; tip: string } = {} as any;

  constructor(
    data?: SalesCommissionInstallmentHistory | SalesCommissionInstallmentHistoryDto,
  ) {
    if (!data) return;
    if (!(data instanceof SalesCommissionInstallmentHistory)) this.fromDto(data);
    else Object.assign(this, data);
  }

  /**
   * Sets the properties of the SalesCommissionInstallmentHistory instance based on the provided DTO.
   * This method is useful for initializing or updating the instance with data from an API response.
   * @param dto - The SalesCommissionInstallmentHistoryDto object containing the data to set.
   * @returns The updated SalesCommissionInstallmentHistory instance.
   */
  fromDto(dto: SalesCommissionInstallmentHistoryDto): SalesCommissionInstallmentHistory {
    if (isNullOrUndefined(dto) || isEmptyValue(dto)) return this;
    this.creditedAt = dateStringToModel(dto.creditedAt);
    this.installment = dto.installment?.toString() ?? '-';
    this.installmentCommissionNetValue = formatCurrency(
      dto.installmentCommissionNetValue,
    );
    this.installmentStatus = this.adaptInstallmentStatus(dto.installmentStatus);

    return this;
  }

  /**
   * Returns a shallow copy of the current SalesCommissionInstallmentHistory instance.
   * Useful for serialization or manipulation without affecting the original object.
   */
  toRaw(): SalesCommissionInstallmentHistory {
    return { ...this };
  }

  /**
   * Adapts the installment status from a numeric value to a more readable string format.
   * @param installmentStatus - The numeric status of the installment.
   * @returns The adapted installment status as a string.
   */
  private adaptInstallmentStatus(installmentStatus: unknown) {
    if (!installmentStatus || isNaN(parseInt(installmentStatus as any)))
      return {
        color: 'help',
        content: 'N/A',
      } as (typeof COMMISSION_STATUS)[keyof typeof COMMISSION_STATUS];

    const statusMap: Record<number, string> = {
      1: 'AVAILABLE',
      2: 'CREDITED',
      3: 'DEDUCTED',
      4: 'DEBITED',
      5: 'CANCELED',
      6: 'PREVIEW',
    };

    return COMMISSION_STATUS[
      statusMap[
        installmentStatus as keyof typeof statusMap
      ] as keyof typeof COMMISSION_STATUS
    ];
  }
}

/**
 * Represents a Data Transfer Object (DTO) for sales commission, including invoice details,
 */
export type SalesCommissionDto = {
  invoiceLegacyId: null | number;
  invoiceNumber: null | string;
  invoiceSerieNumber: null | string;
  invoiceTotalAmount: null | number;
  billingDate: null | string;
  finalCustomerName: null | string;
  finalCustomerDocument: null | string;
  salesOrderLegacyId: null | number;
  salesCommissionInstallmentsTotals: {
    installments: SalesCommissionInstallmentDto[];
    installmentsValueAmount: SalesCommissionInstallmentTotalsDto['installmentsValueAmount'];
    installmentsCommissionNetValueAmount: SalesCommissionInstallmentTotalsDto['installmentsCommissionNetValueAmount'];
  };

  salesCommissionInstallmentsHistory: SalesCommissionInstallmentHistoryDto[];
  installmentStatus: string | null;
  salesCommissionItemsTotals: {
    salesCommissionItems: SalesCommissionItemDto[];
    ipiTotalValue: SalesCommissionItemTotalsDto['ipiTotalValue'];
    ipiTotalPercentage: SalesCommissionItemTotalsDto['ipiTotalPercentage'];
    issTotalValue: SalesCommissionItemTotalsDto['issTotalValue'];
    issTotalPercentage: SalesCommissionItemTotalsDto['issTotalPercentage'];
    icmsTotalValue: SalesCommissionItemTotalsDto['icmsTotalValue'];
    icmsTotalPercentage: SalesCommissionItemTotalsDto['icmsTotalPercentage'];
    pisTotalValue: SalesCommissionItemTotalsDto['pisTotalValue'];
    pisTotalPercentage: SalesCommissionItemTotalsDto['pisTotalPercentage'];
    cofinsTotalValue: SalesCommissionItemTotalsDto['cofinsTotalValue'];
    cofinsTotalPercentage: SalesCommissionItemTotalsDto['cofinsTotalPercentage'];
  };
};

/**
 * Represents a sales commission, including invoice, customer, installments, and item totals.
 * Provides methods to initialize, update, and clear data from DTOs or other SalesCommission instances.
 */
export class SalesCommission {
  invoiceLegacyId: string = '';
  salesOrderLegacyId: string = '';
  invoiceNumber: string = '';
  invoiceSerie: string = '';
  invoiceAmount: string = '';
  customerName: string = '';
  customerDocument: CompanyDocument | PersonalDocument | string = '';
  billingDate: Date | string = '';
  // #region Installments
  installments: Array<SalesCommissionInstallment> =
    new Array<SalesCommissionInstallment>();
  installmentsAmount: SalesCommissionInstallmentTotals['installmentsAmount'] = '-';
  installmentsNetValueAmount: SalesCommissionInstallmentTotals['netValueAmount'] = '-';
  // #endregion Installments

  // #region Installments History
  installmentsHistory: Array<SalesCommissionInstallmentHistory> =
    new Array<SalesCommissionInstallmentHistory>();
  // #endregion Installments History

  // #region Installment Totals
  salesCommissionItems: SalesCommissionItem[] = new Array<SalesCommissionItem>();
  ipiTotalValue: SalesCommissionItemTotals['ipiTotalValue'] = '-';
  ipiTotalPercentage: SalesCommissionItemTotals['ipiTotalPercentage'] = '';
  issTotalValue: SalesCommissionItemTotals['issTotalValue'] = '-';
  issTotalPercentage: SalesCommissionItemTotals['issTotalPercentage'] = '';
  icmsTotalValue: SalesCommissionItemTotals['icmsTotalValue'] = '-';
  icmsTotalPercentage: SalesCommissionItemTotals['icmsTotalPercentage'] = '';
  pisTotalValue: SalesCommissionItemTotals['pisTotalValue'] = '-';
  pisTotalPercentage: SalesCommissionItemTotals['pisTotalPercentage'] = '';
  cofinsTotalValue: SalesCommissionItemTotals['cofinsTotalValue'] = '-';
  cofinsTotalPercentage: SalesCommissionItemTotals['cofinsTotalPercentage'] = '';
  // #endregion Installment Totals

  /**
   * Returns the totals of sales commission installments, including the total amount and net value.
   */
  get installmentsTotals(): InstallmentsTotals {
    return Object.keys(this.toRaw())
      .map((key) => key as keyof InstallmentsTotals)
      .reduce((acc, key) => {
        if (
          key === 'installments' ||
          key.endsWith('Amount') ||
          key.endsWith('NetValueAmount')
        )
          acc[key] = (this as any)[key];

        return acc;
      }, {} as InstallmentsTotals);
  }

  /**
   * Returns the totals of sales commission items, including various tax values and percentages.
   */
  get itemsTotals(): ItemsTotals {
    return Object.keys(this.toRaw())
      .map((key) => key as keyof SalesCommission)
      .reduce((acc, key) => {
        if (
          key === 'salesCommissionItems' ||
          key.endsWith('TotalValue') ||
          key.endsWith('TotalPercentage')
        )
          (acc as any)[key] = this[key];

        return acc;
      }, {} as ItemsTotals);
  }

  constructor(data?: SalesCommission | SalesCommissionDto) {
    if (!data) return;
    if (!(data instanceof SalesCommission)) {
      this.setDataFromDto(data as SalesCommissionDto);
    } else {
      Object.assign(this, data);
    }
  }

  /**
   * Sets the properties of the SalesCommission instance based on the provided data.
   * This method is useful for initializing or updating the instance with data from an API response.
   * @param data - The SalesCommission object containing the data to set.
   * @returns The updated SalesCommission instance.
   */
  setData(data: SalesCommission) {
    if (!data) return this;

    Object.assign(this, data);
    return this;
  }

  /**
   * Sets the properties of the SalesCommission instance based on the provided DTO.
   * This method is useful for initializing or updating the instance with data from an API response.
   * @param dto - The SalesCommissionDto object containing the data to set.
   * @returns The updated SalesCommission instance.
   */
  setDataFromDto(dto: SalesCommissionDto) {
    if (!dto) return this;
    this.invoiceLegacyId = dto.invoiceLegacyId?.toString() ?? '';
    this.salesOrderLegacyId = dto.salesOrderLegacyId?.toString() ?? '';
    this.invoiceNumber = dto.invoiceNumber ?? '';
    this.invoiceSerie = dto.invoiceSerieNumber ?? '';
    this.invoiceAmount = formatCurrency(dto.invoiceTotalAmount);
    this.customerName = dto.finalCustomerName ?? '';
    this.customerDocument = adaptCustomerDocument(dto.finalCustomerDocument);
    this.billingDate = dateStringToModel(dto.billingDate);

    // #region Installments
    this.installments =
      dto.salesCommissionInstallmentsTotals?.installments?.map((installment) =>
        new SalesCommissionInstallment(installment).toRaw(),
      ) ?? new Array<SalesCommissionInstallment>();

    const installmentTotals = new SalesCommissionInstallmentTotals({
      installmentsValueAmount:
        dto.salesCommissionInstallmentsTotals?.installmentsValueAmount!,
      installmentsCommissionNetValueAmount:
        dto.salesCommissionInstallmentsTotals?.installmentsCommissionNetValueAmount,
    }).toRaw();

    this.installmentsAmount = installmentTotals.installmentsAmount;
    this.installmentsNetValueAmount = installmentTotals.netValueAmount;
    // #endregion Installments

    // #region Installments History
    this.installmentsHistory =
      dto.salesCommissionInstallmentsHistory?.map((history) =>
        new SalesCommissionInstallmentHistory(history).toRaw(),
      ) ?? new Array<SalesCommissionInstallmentHistory>();
    // #endregion Installments History

    // #region Installment Totals
    const { salesCommissionItems } = dto.salesCommissionItemsTotals;
    this.salesCommissionItems =
      salesCommissionItems?.map((item) => new SalesCommissionItem(item).toRaw()) ??
      new Array<SalesCommissionItem>();

    const itemTotals = new SalesCommissionItemTotals(
      Object.keys(dto.salesCommissionItemsTotals)
        .map((key) => key as keyof SalesCommissionDto['salesCommissionItemsTotals'])
        .reduce((acc: SalesCommissionItemTotalsDto, key) => {
          if (key !== 'salesCommissionItems')
            acc[key] = dto.salesCommissionItemsTotals[key];

          return acc;
        }, {} as SalesCommissionItemTotalsDto),
    ).toRaw();

    getObjectKeys<SalesCommissionItemTotals>(itemTotals).forEach((key) => {
      (this as any)[key] = itemTotals[key];
    });
    // #endregion Installment Totals

    return this;
  }

  /**
   * Returns a shallow copy of the current SalesCommission instance.
   * Useful for serialization or manipulation without affecting the original object.
   */
  toRaw(): SalesCommission {
    return { ...this };
  }

  /**
   * Adapts the customer document from a string to a more structured format.
   * @param document - The customer document string.
   * @returns The adapted customer document.
   */
  clearData() {
    Object.assign(this, new SalesCommission());
    return this;
  }
}

export type InstallmentsTotals = Pick<
  SalesCommission,
  'installments' | 'installmentsAmount' | 'installmentsNetValueAmount'
>;

export type ItemsTotals = Pick<
  SalesCommission,
  | 'salesCommissionItems'
  | 'ipiTotalValue'
  | 'ipiTotalPercentage'
  | 'issTotalValue'
  | 'issTotalPercentage'
  | 'icmsTotalValue'
  | 'icmsTotalPercentage'
  | 'pisTotalValue'
  | 'pisTotalPercentage'
  | 'cofinsTotalValue'
  | 'cofinsTotalPercentage'
>;
